import React from 'react';
import { Settings, Footprints, Dumbbell, Heart, Moon } from 'lucide-react';

/**
 * Секция расширенных опций для более точного расчета TDEE
 */
const AdvancedOptionsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  if (!formData.includeAdvancedOptions) {
    return (
      <div className="mb-6">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeAdvancedOptions"
            checked={formData.includeAdvancedOptions}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Включить расширенные опции для более точного расчета
          </span>
        </label>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex items-center mb-4">
        <input
          type="checkbox"
          id="includeAdvancedOptions"
          name="includeAdvancedOptions"
          checked={formData.includeAdvancedOptions}
          onChange={handleInputChange}
          className="form-checkbox"
        />
        <label htmlFor="includeAdvancedOptions" className="ml-2 text-base font-medium text-gray-900 dark:text-white flex items-center">
          <Settings className="w-5 h-5 mr-2 text-emerald-500" />
          Расширенные опции для точного расчета
        </label>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Среднее количество шагов в день */}
          <div>
            <label htmlFor="dailySteps" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Footprints className="w-4 h-4 mr-2 text-blue-500" />
              Шагов в день
            </label>
            <input
              type="text"
              id="dailySteps"
              name="dailySteps"
              value={formData.dailySteps}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.dailySteps ? 'input-error' : ''}`}
              placeholder="Среднее количество шагов"
            />
            {errors.dailySteps && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.dailySteps}
              </p>
            )}
            <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
              Среднее значение: 7500 шагов
            </p>
          </div>

          {/* Количество тренировок в неделю */}
          <div>
            <label htmlFor="workoutDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Dumbbell className="w-4 h-4 mr-2 text-blue-500" />
              Дней тренировок в неделю
            </label>
            <input
              type="text"
              id="workoutDays"
              name="workoutDays"
              value={formData.workoutDays}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.workoutDays ? 'input-error' : ''}`}
              placeholder="Количество дней"
            />
            {errors.workoutDays && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.workoutDays}
              </p>
            )}
          </div>

          {/* Интенсивность тренировок */}
          <div>
            <label htmlFor="workoutIntensity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Интенсивность тренировок
            </label>
            <select
              id="workoutIntensity"
              name="workoutIntensity"
              value={formData.workoutIntensity}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              <option value="light">Легкая (прогулки, йога, растяжка)</option>
              <option value="moderate">Умеренная (бег, плавание, фитнес)</option>
              <option value="intense">Интенсивная (силовые, HIIT, кроссфит)</option>
              <option value="very_intense">Очень интенсивная (профессиональный спорт)</option>
            </select>
          </div>

          {/* Уровень стресса */}
          <div>
            <label htmlFor="stress" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Heart className="w-4 h-4 mr-2 text-blue-500" />
              Уровень стресса
            </label>
            <select
              id="stress"
              name="stress"
              value={formData.stress}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              <option value="low">Низкий (редкий или слабый стресс)</option>
              <option value="moderate">Умеренный (периодический стресс)</option>
              <option value="high">Высокий (хронический или сильный стресс)</option>
            </select>
            <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
              Стресс влияет на метаболизм и потребность в калориях
            </p>
          </div>

          {/* Количество часов сна */}
          <div>
            <label htmlFor="sleepHours" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Moon className="w-4 h-4 mr-2 text-blue-500" />
              Часов сна в сутки
            </label>
            <input
              type="text"
              id="sleepHours"
              name="sleepHours"
              value={formData.sleepHours}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.sleepHours ? 'input-error' : ''}`}
              placeholder="Среднее количество часов"
            />
            {errors.sleepHours && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.sleepHours}
              </p>
            )}
            <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
              Оптимально: 7-8 часов для взрослого человека
            </p>
          </div>
        </div>

        <div className="mt-4 bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start">
            <Settings className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-1" />
            <div>
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                Зачем нужны расширенные параметры?
              </h4>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Эти параметры помогают точнее определить ваш TDEE, учитывая
                образ жизни и физическую активность. Факторы вроде стресса и сна 
                могут значительно влиять на метаболизм и потребность в калориях.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedOptionsSection;